import { getLoginLink } from '@verbit-ai/frontend-common'
import { IInitConfig } from 'flagsmith/types'
import { ProxyTarget } from 'proxy.config'

import { MOCK_URLS_BY_DEMO_JOB_ID, isDemoJobId } from 'src/models/Demo'
import { ThemeType } from 'src/themes/types'

import { getLanguageByCode } from './language'

const getBooleanQueryParam = (paramName: string, defaultValue: boolean) => {
    const urlParams = new URLSearchParams(window.location.search)
    let paramValue = defaultValue

    try {
        paramValue = !!JSON.parse(urlParams.get(paramName) || '')
    } catch (e) {}

    return paramValue
}

export type Env = 'dev' | 'staging' | 'production'
export type SourceOfTruth = 'platform' | 'trax'

export const getEnv = (): Env => import.meta.env.VITE_ENV ?? 'dev'
export const getProxyTarget = (): ProxyTarget => import.meta.env.VITE_PROXY_TARGET ?? 'staging'

const ENV_SEGMENT: Record<Env, string> = {
    production: 'prod',
    staging: 'staging',
    dev: 'dev',
}

/**
 * Env segment for cache keys, Ably channel names, and other backend-scoped identifiers.
 *
 * `VITE_ENV_SEGMENT` overrides it, so a local stack can match rt-broker's own `ENVIRONMENT`
 * on `transcript:{segment}:{job}:{lang}:crdt`.
 */
export const getEnvSegment = (): string => {
    const override = import.meta.env.VITE_ENV_SEGMENT
    if (override) {
        return override
    }

    const env = getEnv()
    if (env === 'dev') {
        const proxyTarget = getProxyTarget()

        return proxyTarget === 'local' ? 'staging' : (ENV_SEGMENT[proxyTarget] ?? proxyTarget)
    }

    return ENV_SEGMENT[env] ?? env
}

export const getAwsRegion = (): string => import.meta.env.VITE_AWS_REGION
export const getAlternativeRegionHost = () => {
    const isProduction = getEnv() === 'production'
    const isUS = getAwsRegion().startsWith('us')

    if (isProduction) {
        return isUS ? 'https://eu-transcript.verbit.co/' : 'https://transcript.verbit.co/'
    }

    return isUS ? 'https://transcript-staging.verbit.co/' : 'https://us-transcript-staging.verbit.co/'
}
export const getAlternativeRegionUrl = () => `${getAlternativeRegionHost()}${location.search}`

export const getPlatformServerUrl = () => import.meta.env.VITE_PLATFORM_SERVER_URL

export const getTranscriptServerUrl = () => import.meta.env.VITE_TRANSCRIPT_SERVER_URL

export const getVerbitAuthServerUrl = () => import.meta.env.VITE_VERBIT_AUTH_SERVER_URL

export const getRtBrokerWsServerUrl = () => import.meta.env.VITE_RT_BROKER_WS_SERVER_URL

export const getVisorServerUrl = () => import.meta.env.VITE_VISOR_SERVER_URL

export const getOrderingServerUrl = () => import.meta.env.VITE_ORDERING_SERVER_URL

export const getOrderingFrontendUrl = () => import.meta.env.VITE_ORDERING_FRONTEND_URL

export const getLoginAppUrl = () => import.meta.env.VITE_LOGIN_APP_URL

export const redirectToLogin = () => {
    window.location.href = getLoginLink(getLoginAppUrl())
}

export const getLogRocketApiKey = () => import.meta.env.VITE_LOG_ROCKET_API_KEY

export const getMixpanelApiKey = () => import.meta.env.VITE_MIXPANEL_API_KEY

export const getRollbarToken = () => import.meta.env.VITE_ROLLBAR_TOKEN

export const logger = (...data: any[]) => {
    if (getEnv() !== 'production') {
        console.log(...data)
    }
}

const TRANSCRIPT_JOB_ID_QUERY_PARAM = 'transcriptJobId'
export const getTranscriptJobId = (): string => {
    if (isMockActive()) {
        return document.mocks.getTranscriptJobId()
    }

    const urlParams = new URLSearchParams(window.location.search)

    return urlParams.get(TRANSCRIPT_JOB_ID_QUERY_PARAM) ?? ''
}

const TRANSCRIPTION_DOCUMENT_ID_QUERY_PARAM = 'transcriptionDocumentId'
export const getTranscriptionDocumentId = (): string => {
    const urlParams = new URLSearchParams(window.location.search)

    const documentIdFromUrl = urlParams.get(TRANSCRIPTION_DOCUMENT_ID_QUERY_PARAM)
    if (documentIdFromUrl) {
        return documentIdFromUrl
    }

    // for mocks activated via `?mocks=` without explicit job / document ID,
    // return mock document ID when mock metadata indicates document view mode
    if (
        isMockActive() &&
        !urlParams.get(TRANSCRIPT_JOB_ID_QUERY_PARAM) &&
        document.mocks?.getTranscriptMetadata()?.viewMode === 'document'
    ) {
        return document.mocks.getTranscriptionDocumentId()
    }

    return ''
}

/**
 * Inconsistency uuid a Visor deep link points at, handed to the insights panel so it opens
 * on that card. The param name is the insights panel's contract (`ai-service-web`), and Visor
 * builds links with it in `buildTranscriptUrl`.
 */
const INCONSISTENCY_ID_QUERY_PARAM = 'inconsistencyId'
export const getDeepLinkInconsistencyId = (): string | undefined => {
    const urlParams = new URLSearchParams(window.location.search)

    // `?inconsistencyId=` (present but empty) is not a deep link, so treat it like an absent param.
    return urlParams.get(INCONSISTENCY_ID_QUERY_PARAM) || undefined
}

export type ViewMode = 'job' | 'document'

export const getViewMode = (): ViewMode | null => {
    const urlParams = new URLSearchParams(window.location.search)
    if (urlParams.get(TRANSCRIPT_JOB_ID_QUERY_PARAM)) {
        return 'job'
    }
    if (urlParams.get(TRANSCRIPTION_DOCUMENT_ID_QUERY_PARAM)) {
        return 'document'
    }

    if (isMockActive()) {
        return document.mocks?.getTranscriptMetadata()?.viewMode ?? 'job'
    }

    return null
}

export const getTraxSessionId = (): string => {
    const urlParams = new URLSearchParams(window.location.search)

    return urlParams.get('traxSessionId') ?? ''
}

export const getSourceOfTruth = (): SourceOfTruth => {
    const urlParams = new URLSearchParams(window.location.search)
    const sotValue = urlParams.get('sot') || 'platform'

    return sotValue === 'trax' ? 'trax' : 'platform'
}

export const getCollectionId = () => {
    const urlParams = new URLSearchParams(window.location.search)
    const collectionId = urlParams.get('collectionId')

    return collectionId
}

const MOCKS_QUERY_PARAM = 'mocks'
export const isMockActive = () => {
    const urlParams = new URLSearchParams(window.location.search)

    const transcriptJobId = urlParams.get(TRANSCRIPT_JOB_ID_QUERY_PARAM)
    if (isDemoJobId(transcriptJobId)) {
        return true
    }

    return urlParams.get(MOCKS_QUERY_PARAM) !== null
}

export const getMockFileUrl = () => {
    const urlParams = new URLSearchParams(window.location.search)

    const transcriptJobId = urlParams.get(TRANSCRIPT_JOB_ID_QUERY_PARAM)
    if (isDemoJobId(transcriptJobId)) {
        const { defaultUrl, translations } = MOCK_URLS_BY_DEMO_JOB_ID[transcriptJobId]
        const languageCode = urlParams.get(REQUESTED_LANGUAGE_PARAM)
        const translationUrl = languageCode ? translations[getLanguageByCode(languageCode)] : ''

        return translationUrl || defaultUrl
    }

    return urlParams.get(MOCKS_QUERY_PARAM)!
}

export const THEME_TYPE_QUERY_PARAM = 'themeType'
/**
 * When chromakey mode is active:
 *  - chromakey theme is selected, regardless of saved user preferences;
 *  - theme type cannot be changed, and text color preferences are not saved to local storage;
 *  - following UI elements are not displayed: header, search button, download button,
 *    speaker names, timestamps, AI panel (even if enabled for current customer);
 *  - font size and text color controls look differently
 */
export const isChromakeyModeActive = (() => {
    const urlParams = new URLSearchParams(window.location.search)
    const chromakeyThemeType: ThemeType = 'chromakey'

    return urlParams.get(THEME_TYPE_QUERY_PARAM) === chromakeyThemeType
})()

export const TEXT_COLOR_INDEX_QUERY_PARAM = 'textColorIndex'
export const TEXT_BACKGROUND_COLOR_INDEX_QUERY_PARAM = 'textBackgroundColorIndex'
export const APP_BACKGROUND_COLOR_INDEX_QUERY_PARAM = 'appBackgroundColorIndex'
export const FONT_FAMILY_QUERY_PARAM = 'fontFamily'
export const FONT_SIZE_PERCENTAGE_QUERY_PARAM = 'fontSizePercentage'
export const IS_HEADER_HIDDEN_QUERY_PARAM = 'isHeaderHidden'
export const IS_FULL_WIDTH_MODE_QUERY_PARAM = 'isFullWidthMode'
export const SHOULD_DISPLAY_LOGO_QUERY_PARAM = 'shouldDisplayLogo'
export const SHOULD_SHOW_TRANSCRIPT_IN_ALL_CAPS_QUERY_PARAM = 'shouldShowTranscriptInAllCaps'
export const IS_EDITING_LOCKED_QUERY_PARAM = 'isEditingLocked'

export const isAppOpenedFromEmailLink = () => {
    const urlParams = new URLSearchParams(window.location.search)

    return urlParams.get('source') === 'email'
}

export const isZoomAppsEnv = () => navigator.userAgent.includes('ZoomApps')

export const getFlagsmithApiKey = () => import.meta.env.VITE_FLAGSMITH_API_KEY

export const getSendbirdAppId = (): string | undefined => import.meta.env.VITE_SENDBIRD_APP_ID
export const getSendbirdAiAgentId = (): string | undefined => import.meta.env.VITE_SENDBIRD_AI_AGENT_ID

// Points the Flagsmith SDK at a self-hosted backend (or same-origin `/flagsmithApi`
// proxy path) instead of SaaS. Unset => SDK default, behaviour unchanged. The SDK
// concatenates `flags/`/`identities/` directly onto `api` (its own default is
// `.../api/v1/`), so a bare host or proxy path needs `/api/v1/` appended here too.
export const getFlagsmithConfigOverrides = (): Partial<IInitConfig<string, string>> => {
    const apiUrl = import.meta.env.VITE_FLAGSMITH_API_URL

    if (!apiUrl) {
        return {}
    }

    const base = apiUrl.replace(/\/+$/, '')

    return { api: base.endsWith('/api/v1') ? `${base}/` : `${base}/api/v1/` }
}

export const REQUESTED_LANGUAGE_PARAM = 'language'

/**
 * Query parameter that controls the UI display mode (how content is rendered).
 *
 * @example ?displayMode=document - Shows PDF preview of the document
 * @example ?displayMode=<anything else or omitted> - Shows transcript text view
 *
 * Note: This is different from `TranscriptMetadata.viewMode`, which determines
 * the data source:
 * - `TranscriptMetadata.viewMode = 'job'` - Data from a regular transcription job
 * - `TranscriptMetadata.viewMode = 'document'` - Data from an ingested document
 *
 * The display mode controls UI presentation, while TranscriptMetadata.viewMode
 * controls which API endpoints and providers are used to fetch the data.
 */
export const DISPLAY_MODE_PARAM = 'displayMode'
export const DOCUMENT_DISPLAY_MODE = 'document'

export const isPdfDisplayMode = () => {
    const urlParams = new URLSearchParams(window.location.search)
    const displayMode = urlParams.get(DISPLAY_MODE_PARAM)

    return displayMode === DOCUMENT_DISPLAY_MODE
}

export const IS_USER_SPEAKER_PARAM = 'isSpeaker'
export const isUserSpeaker = () => getBooleanQueryParam(IS_USER_SPEAKER_PARAM, false)

export const isMockAutoplayRequested = () => {
    const urlParams = new URLSearchParams(window.location.search)
    const transcriptJobId = urlParams.get(TRANSCRIPT_JOB_ID_QUERY_PARAM)

    return isDemoJobId(transcriptJobId) ? true : getBooleanQueryParam('autoPlay', false)
}
export const getMockAutoplayDelay = () => {
    const urlParams = new URLSearchParams(window.location.search)
    const startAfter = Number(urlParams.get('startAfter'))

    return isNaN(startAfter) ? 0 : startAfter
}

export const isMockAutoRefreshRequested = () => getBooleanQueryParam('autoRefresh', false)

export const isMockPlayerDisabled = () => {
    const urlParams = new URLSearchParams(window.location.search)
    const transcriptJobId = urlParams.get(TRANSCRIPT_JOB_ID_QUERY_PARAM)

    return transcriptJobId ? true : getBooleanQueryParam('disableMockPlayer', false)
}

export const isMockDiarizationEnabled = () => getBooleanQueryParam('withDiarization', false)

export const isInvalidToken = () => getBooleanQueryParam('invalidToken', false)
export const isNoFutureSessions = () => getBooleanQueryParam('noFutureSessions', false)
